import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-grid bg-grid-fade">
        <div className="w-full max-w-md surface p-8" data-testid="forgot-card">
          <div className="section-label mb-2">— PASSWORD RESET</div>
          <h1 className="font-chivo font-black text-3xl text-white uppercase mb-4">Forgot Password</h1>
          {sent ? (
            <div className="font-plex text-sm text-[#9CA3AF]" data-testid="forgot-success">
              If an account exists for <strong className="text-white">{email}</strong>, a reset link has been sent. Check your inbox.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Your email" className="input-rugged" data-testid="forgot-email-input" />
              {error && <div className="text-sm font-mono text-[#EF4444]">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit-button">
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
          )}
          <div className="mt-6 font-mono text-xs text-center">
            <Link to="/login" className="text-[#00D4FF] hover:underline">← Back to login</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. You can log in now.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-grid bg-grid-fade">
        <div className="w-full max-w-md surface p-8" data-testid="reset-card">
          <h1 className="font-chivo font-black text-3xl text-white uppercase mb-4">Reset Password</h1>
          {done ? (
            <div className="font-plex text-sm text-[#9CA3AF]">
              Password reset successfully. <Link to="/login" className="text-[#00D4FF]">Login here</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="New password" className="input-rugged" data-testid="reset-password-input" />
              {error && <div className="text-sm font-mono text-[#EF4444]">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-button">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
